import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dispatch } from 'redux'
import { DefaultRootState, useSelector } from 'react-redux'
import { useFlexFlagIsOn } from '@indeed/flex-feature-flags'
import { useTrackingTrigger } from '@indeed/flex-tracking-context'

import { ShiftEntity } from 'syft-acp-core/store/types'
import { entityList$ } from 'syft-acp-core/reducers/generators/entities'
import { fetchEmployerPreferences } from 'syft-acp-core/store/employerPreferences/actions'
import { hideAllAreasOrVenues } from 'syft-acp-core/entities/ListingDetail/ListingShiftTabs/helpers'

import { FLEX_CASCADE_PREFERENCES_FLAG, FLEX_DISABLED_WARNING_FLAG } from './FlexDisabledWarning'
import { trackingEvents } from './ShiftFulfilmentModal.tracking'
import { shouldWarnFlexDisabled } from './ShiftFulfilmentModal.helpers'

type UseFlexDisabledWarningArgs = {
  shift: Partial<ShiftEntity>
  shiftID?: number
  stateShiftId?: number
  isShown?: boolean
  offerToFlex?: string | null
  dispatch: Dispatch
}

export const useFlexDisabledWarning = ({
  shift,
  shiftID,
  stateShiftId,
  isShown,
  offerToFlex,
  dispatch,
}: UseFlexDisabledWarningArgs) => {
  const isWarningFlagOn = useFlexFlagIsOn(FLEX_DISABLED_WARNING_FLAG)
  const isCascadeFlagOn = useFlexFlagIsOn(FLEX_CASCADE_PREFERENCES_FLAG)
  const isEnabled = isWarningFlagOn && isCascadeFlagOn

  const employerId = shift?.employer?.id
  const venueId = shift?.venue?.id
  const isSingleVenue = hideAllAreasOrVenues('venue')(shift)

  const employerPreferencesState = useSelector((rootState: DefaultRootState) => rootState.employerPreferences)

  const preferences = useMemo(
    () =>
      employerId && venueId
        ? entityList$(employerPreferencesState ?? {}, {
            employer_id: employerId,
            work_location_id: venueId,
          })
        : [],
    [employerPreferencesState, employerId, venueId],
  )

  useEffect(() => {
    if (isEnabled && isShown && employerId && venueId) {
      dispatch(fetchEmployerPreferences({ options: { employer_id: employerId, work_location_id: venueId } }))
    }
  }, [isEnabled, isShown, employerId, venueId, dispatch])

  const isStateForSelectedShift = stateShiftId === shiftID

  const shouldWarn =
    isStateForSelectedShift && shouldWarnFlexDisabled({ isEnabled, offerToFlex, preferences, isSingleVenue })
  const trackingPayload = { shift_id: shiftID, venue_id: venueId, offer_to_flex: offerToFlex }

  const isWarningVisible = shouldWarn && !!isShown
  const [wasWarningVisible, setWasWarningVisible] = useState(isWarningVisible)
  const [appearanceKey, setAppearanceKey] = useState(0)
  if (wasWarningVisible !== isWarningVisible) {
    setWasWarningVisible(isWarningVisible)
    if (isWarningVisible) setAppearanceKey(key => key + 1)
  }

  const [acknowledgedKey, setAcknowledgedKey] = useState<number | null>(null)
  const isAcknowledged = acknowledgedKey === appearanceKey
  const setIsAcknowledged = useCallback(
    (checked: boolean) => setAcknowledgedKey(checked ? appearanceKey : null),
    [appearanceKey],
  )

  const triggerEvent = useTrackingTrigger()
  const reportedAppearanceKey = useRef<number | null>(null)
  const reportWarningShown = useCallback(() => {
    if (reportedAppearanceKey.current === appearanceKey) return

    reportedAppearanceKey.current = appearanceKey
    triggerEvent(trackingEvents.SHIFT_FULFILMENT.FLEX_DISABLED_WARNING.VIEWED, {
      shift_id: shiftID,
      venue_id: venueId,
      offer_to_flex: offerToFlex,
    })
  }, [appearanceKey, shiftID, venueId, offerToFlex, triggerEvent])

  return { shouldWarn, isAcknowledged, setIsAcknowledged, trackingPayload, reportWarningShown }
}
